import { registerPlugin } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';

/**
 * Local wrapper for the native Agora RTC plugin.
 * This avoids needing capacitor-agora-rtc as an npm dependency -
 * the native plugin is registered at runtime by Capacitor from the
 * compiled iOS/Android code. On web, these calls are no-ops since
 * the web hooks already use the Agora Web SDK directly.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

export interface InitializeOptions {
  appId: string;
  enableAudio?: boolean;
  enableVideo?: boolean;
}

export interface JoinChannelOptions {
  token: string | null;
  channelName: string;
  uid?: number;
  publishAudio?: boolean;
  publishVideo?: boolean;
  enableAudio?: boolean;
  enableVideo?: boolean;
}

export interface VideoViewOptions {
  viewId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex?: number;
  mirror?: boolean;
  renderMode?: string;
  borderRadius?: number;
}

export interface RemoteVideoViewOptions extends VideoViewOptions {
  uid: number;
}

export interface UserJoinedEvent {
  uid: number;
}

export interface UserLeftEvent {
  uid: number;
  reason?: string;
}

export interface UserAudioStateEvent {
  uid: number;
  muted: boolean;
}

export interface UserVideoStateEvent {
  uid: number;
  enabled: boolean;
}

export interface ConnectionStateEvent {
  state: ConnectionState;
  reason?: string;
}

export interface ActiveSpeakerEvent {
  uid: number;
}

export interface VolumeSpeaker {
  uid: number;
  volume: number;
}

export interface VolumeIndicationEvent {
  speakers: VolumeSpeaker[];
  totalVolume?: number;
}

export interface ErrorEvent {
  code: number;
  message: string;
}

// ============================================================================
// PLUGIN INTERFACE
// ============================================================================

export interface AgoraRtcPlugin {
  initialize(options: InitializeOptions): Promise<void>;
  joinChannel(options: JoinChannelOptions): Promise<{ uid: number }>;
  leaveChannel(): Promise<void>;
  setAudioMuted(options: { muted: boolean }): Promise<void>;
  setVideoEnabled(options: { enabled: boolean }): Promise<void>;
  setRemoteAudioMuted(options: { uid: number; muted: boolean }): Promise<void>;
  setRemoteVideoMuted(options: { uid: number; muted: boolean }): Promise<void>;
  switchCamera(): Promise<void>;
  setSpeakerphone(options: { enabled: boolean }): Promise<void>;
  setupLocalVideo(options: VideoViewOptions): Promise<void>;
  setupRemoteVideo(options: RemoteVideoViewOptions): Promise<void>;
  updateVideoView(options: VideoViewOptions | RemoteVideoViewOptions): Promise<void>;
  removeVideoView(options: { viewId: string }): Promise<void>;
  removeAllVideoViews(): Promise<void>;
  destroy(): Promise<void>;
  addListener(eventName: 'userJoined', listenerFunc: (event: UserJoinedEvent) => void): Promise<{ remove: () => Promise<void> }>;
  addListener(eventName: 'userLeft', listenerFunc: (event: UserLeftEvent) => void): Promise<{ remove: () => Promise<void> }>;
  addListener(eventName: 'userAudioStateChanged', listenerFunc: (event: UserAudioStateEvent) => void): Promise<{ remove: () => Promise<void> }>;
  addListener(eventName: 'userVideoStateChanged', listenerFunc: (event: UserVideoStateEvent) => void): Promise<{ remove: () => Promise<void> }>;
  addListener(eventName: 'connectionStateChanged', listenerFunc: (event: ConnectionStateEvent) => void): Promise<{ remove: () => Promise<void> }>;
  addListener(eventName: 'activeSpeaker', listenerFunc: (event: ActiveSpeakerEvent) => void): Promise<{ remove: () => Promise<void> }>;
  addListener(eventName: 'volumeIndication', listenerFunc: (event: VolumeIndicationEvent) => void): Promise<{ remove: () => Promise<void> }>;
  addListener(eventName: 'error', listenerFunc: (event: ErrorEvent) => void): Promise<{ remove: () => Promise<void> }>;
  addListener(eventName: string, listenerFunc: (event: any) => void): Promise<{ remove: () => Promise<void> }>;
  removeAllListeners(): Promise<void>;
}

// ============================================================================
// PLUGIN REGISTRATION
// ============================================================================

const AgoraRtc = registerPlugin<AgoraRtcPlugin>('AgoraRtc');

// Export both names so all consuming files work:
// - useAgoraMedia.ts uses CapacitorAgoraRtc
// - useAgoraRtc.ts and index.ts use AgoraRtc
const CapacitorAgoraRtc = AgoraRtc;

export { AgoraRtc, CapacitorAgoraRtc, Capacitor };